import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { AlertTooltip } from '@openx/components-core/lib/AlertTooltip/AlertTooltip.tsx';
import { NameCell as SharedNameCell } from '@openx/components-core/lib/NameCell/NameCell.tsx';
import { type SegmentStatus, segmentStatus } from '@openx/types/segments.ts';
import { AudienceStatus } from '@openx/types/targeting/audience.ts';
import { WarningIcon } from '@phosphor-icons/react/Warning';
import { WarningCircleIcon } from '@phosphor-icons/react/WarningCircle';
import { useTranslation } from 'react-i18next';
import type { GetAudiencesListQuery } from '../../../../../graphql/query/audiences/GetAudiencesList.generated.tsx';
import { daysToExpiration } from '../../../utils/daysToExpiration.ts';
import { isSoonToBeExpired } from '../../../utils/isSoonToBeExpired.ts';

type NameCellProps = {
  audience: GetAudiencesListQuery['audience'][number];
  searchPhrase: string;
};

export const NameCell = ({ audience, searchPhrase }: NameCellProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const expiringSoonSegments = audience.audience_segments.filter(({ segment }) =>
    isSoonToBeExpired(segment.expiration_date, segment.status as SegmentStatus)
  );
  const soonestSegmentDaysRemaining = expiringSoonSegments.reduce<number | null>((min, { segment }) => {
    const days = daysToExpiration(segment.expiration_date);

    if (days === null) {
      return min;
    }
    if (min === null || days < min) {
      return days;
    }

    return min;
  }, null);

  const showExpiredAudienceAlert = AudienceStatus.EXPIRED === audience.status;
  const showAudienceExpireSoonAlert = isSoonToBeExpired(
    audience.estimated_expiry_date,
    audience.status as AudienceStatus
  );

  const showExpiredSegmentAlert = audience.audience_segments.some(
    ({ segment }) => segment.status === segmentStatus.EXPIRED
  );
  const showSegmentExpireSoonAlert = expiringSoonSegments.length > 0;

  const showArchivedAlert = audience.audience_segments.some(({ segment }) => segment.is_archived);
  const showExpiredAlert = showExpiredSegmentAlert || showExpiredAudienceAlert;
  const showExpireSoonAlert = showSegmentExpireSoonAlert || showAudienceExpireSoonAlert;

  const archivedSegmentText = t('Audience includes a segment that is archived');
  const expiredSoonSegmentText = t(`Segment is going to expire within the next {count} {dayLabel}`, {
    count: soonestSegmentDaysRemaining ?? 0,
    dayLabel: soonestSegmentDaysRemaining === 1 ? 'day' : 'days',
  });
  const expiredSegmentText = t('Segment data is expired and cannot be used for audience creation.');
  const audienceExpiredText = t('Audience hasn`t been refreshed in more than 90 days and is expired.');
  const audienceSoonWillBeExpiredText = t('Audience is pending expiration due to 90 day expiration window.');

  const joinedExpiredText = `${showExpiredAudienceAlert ? audienceExpiredText : ''} ${
    showExpiredAudienceAlert && showExpiredSegmentAlert ? '\n\n' : ''
  }${showExpiredSegmentAlert ? expiredSegmentText : ''}`;

  const joinedSoonWillBeExpiredText = `${showAudienceExpireSoonAlert ? audienceSoonWillBeExpiredText : ''} ${
    showAudienceExpireSoonAlert && showSegmentExpireSoonAlert ? '\n\n' : ''
  } ${showSegmentExpireSoonAlert ? expiredSoonSegmentText : ''}`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <SharedNameCell pathTo={`/audiences/${audience.id}`} searchPhrase={searchPhrase} name={audience.name} />

      {showArchivedAlert && (
        <AlertTooltip
          color={theme.palette.error.main}
          title={archivedSegmentText}
          icon={<WarningCircleIcon size={20} />}
        />
      )}

      {showExpiredAlert && (
        <AlertTooltip
          color={theme.palette.error.main}
          title={joinedExpiredText}
          icon={<WarningCircleIcon size={20} />}
        />
      )}

      {showExpireSoonAlert && (
        <AlertTooltip
          color={theme.palette.warning.main}
          title={joinedSoonWillBeExpiredText}
          icon={<WarningIcon size={20} />}
        />
      )}
    </Box>
  );
};
